import { useState, useRef, useCallback, useEffect } from 'react';
import { Message } from '../types/chat';
import { useWebSocket } from './useWebSocket';
import api, { getApiBaseUrl } from '../utils/axios';
import { generateMessageId } from '../utils/messageIdGenerator';
import { ModelPreference } from '@ui-library';

// Convert backend StreamingEvent messages to friendly frontend messages
const getFriendlyEventMessage = (message: any): string => {
  const { type, data } = message;
  
  // Check if we have the new structured friendly_message object
  if (data && data.friendly_message) {
    const friendlyMsg = data.friendly_message;
    
    // The message field already contains the appropriate message (start or complete)
    // based on the status field sent from backend
    let displayMessage = friendlyMsg.message || '';
    
    if (friendlyMsg.file) {
      displayMessage = displayMessage ? `${displayMessage} ${friendlyMsg.file}` : friendlyMsg.file;
    }
    
    return displayMessage.trim();
  }
  
  // Fallback: Check for old-style message field
  if (data && data.message && data.message.trim()) {
    return data.message;
  }
  
    return '';
};

export interface UseDevAgentOptions {
  projectId: string;
  onMessageUpdate?: (messages: Message[]) => void;
  onProjectStatsUpdate?: (projectId: string, tokenStats: any) => void;
  onNewMessageWithUrls?: () => void;
}

export interface ConversationState {
  thread_id: string;
  execution_status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'STOPPED_BY_USER' | 'DISCARDED';
  context?: any;
  last_activity: string;
}

export interface ThreadStatus {
  is_active: boolean;
  execution_status: string;
  last_activity?: string;
}

export type WaitingState = 'none' | 'waiting_for_updates' | 'show_resume_discard';
export type WorkflowMode = 'chat' | 'elaboration' | 'development';

export interface ElaborationSession {
  session_id: string;
  current_elaboration: string;
  status: 'active' | 'approved' | 'cancelled';
  iteration_count: number;
}

export function useDevAgent({ projectId, onMessageUpdate, onProjectStatsUpdate, onNewMessageWithUrls }: UseDevAgentOptions) {
  // State management
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  
  // Credential management state
  const [credentialCaptureRequest, setCredentialCaptureRequest] = useState<{
    requestId: string;
    parameters: any[];
    envFilePath: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  
  // Enhanced workflow states
  const [workflowMode, setWorkflowMode] = useState<WorkflowMode>('chat');
  const [elaborationSession, setElaborationSession] = useState<ElaborationSession | null>(null);
  const [isElaborationActive, setIsElaborationActive] = useState(false);
  const [currentElaboration, setCurrentElaboration] = useState<string>('');
  const [tokenSummary, setTokenSummary] = useState<any>(null);
  const [cancelledElaborations, setCancelledElaborations] = useState<Set<string>>(new Set());

  // Debug elaboration state changes
  useEffect(() => {
    console.log('🎯 useDevAgent: isElaborationActive changed to:', isElaborationActive);
  }, [isElaborationActive]);

  // Debug cancelled elaborations
  useEffect(() => {
    console.log('❌ Cancelled elaborations updated:', Array.from(cancelledElaborations));
  }, [cancelledElaborations]);
  
  // New states for reconnection flow
  const [waitingState, setWaitingState] = useState<WaitingState>('none');
  const [isCheckingState, setIsCheckingState] = useState(false);
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState<number>(Date.now());
  const [currentConversationState, setCurrentConversationState] = useState<ConversationState | null>(null);
  
  // Timers and refs
  const waitingTimeoutRef = useRef<number | null>(null);
  const messageTimeoutRef = useRef<number | null>(null);
  const timeoutIdRef = useRef<number>(0); // Unique ID to track valid timeouts
  const isLoadingInitialMessagesRef = useRef(false);
  const currentThreadIdRef = useRef<string | undefined>(currentThreadId);
  
  // WebSocket hook
  const webSocket = useWebSocket();
  
  // Keep ref in sync with currentThreadId state
  useEffect(() => {
    currentThreadIdRef.current = currentThreadId;
  }, [currentThreadId]);
  
  // Simple function to update thinking message
  const updateThinkingMessage = useCallback((newMessage: string) => {
    // Ignore blank/empty messages
    if (!newMessage || newMessage.trim() === '') {
      return;
    }

    setMessages(prev => {
      // Find existing thinking message
      const existingThinking = prev.find(msg => msg.id === 'agent-thinking');

      if (existingThinking) {
        // Update existing thinking message
        return prev.map(msg =>
          msg.id === 'agent-thinking'
            ? { ...msg, thinkingMessage: newMessage, timestamp: new Date() }
            : msg
        );
      } else {
        // Create new thinking message
        const thinkingMessage: Message = {
          id: 'agent-thinking',
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          isThinking: true,
          thinkingMessage: newMessage
        };
        return [...prev, thinkingMessage];
      }
    });
  }, []);
  
  // Check conversation state and thread status
  const checkConversationState = useCallback(async (threadId: string): Promise<{
    conversationState: ConversationState | null;
    threadStatus: ThreadStatus | null;
  }> => {
    try {
      // Check both conversation state and thread status in parallel
      const [conversationResponse, threadStatusResponse] = await Promise.allSettled([
        api.get(`/dev-agent/projects/${projectId}/incomplete-conversations`),
        api.get(`/dev-agent/projects/${projectId}/conversations/${threadId}/status`)
      ]);

      let conversationState: ConversationState | null = null;
      if (conversationResponse.status === 'fulfilled') {
        const incompleteConversations = conversationResponse.value.data.incomplete_conversations || [];
        conversationState = incompleteConversations.find((conv: ConversationState) => conv.thread_id === threadId) || null;
      } else {
        console.log('No incomplete conversations found (normal for new threads)');
      }

      let threadStatus: ThreadStatus | null = null;
      if (threadStatusResponse.status === 'fulfilled') {
        threadStatus = threadStatusResponse.value.data;
      } else {
        console.log('No thread status found (normal for new threads)');
      }

      return { conversationState, threadStatus };
    } catch (error) {
      console.log('Error checking conversation state (normal for new threads):', error);
      return { conversationState: null, threadStatus: null };
    }
  }, [projectId]);

  // Load messages for a thread
  const loadMessages = useCallback(async (threadId: string, force = false, limit = 10, beforeMessageId?: string) => {
    if (isLoadingInitialMessagesRef.current) return;
    
    console.log('📥 LOAD_MESSAGES: Called with threadId:', threadId, 'force:', force);
    console.log('📥 LOAD_MESSAGES: Current state - isLoading:', isLoading, 'isStreaming:', isStreaming, 'messages.length:', messages.length);
    
    // Skip if already loading to prevent duplicate requests
    if (!force && isLoadingInitialMessagesRef.current) {
      console.log('📥 LOAD_MESSAGES: Skipping - already loading');
      return;
    }
    

    
    try {
      isLoadingInitialMessagesRef.current = true;
      console.log('📥 LOAD_MESSAGES: Loading messages for thread:', threadId);
      
      // Build query parameters for pagination
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (beforeMessageId) params.append('before_message_id', beforeMessageId);
      
      const response = await api.get(`/dev-agent/projects/${projectId}/threads/${threadId}/messages/?${params.toString()}`);
      
      // Debug: Log the raw API response
      console.log('📥 RAW API RESPONSE:', {
        status: response.status,
        statusText: response.statusText,
        messagesCount: response.data.messages?.length || 0,
        pagination: response.data.pagination,
        elaborationSessionStatus: response.data.elaboration_session_status,
        firstMessage: response.data.messages?.[0],
        lastMessage: response.data.messages?.[response.data.messages?.length - 1],
        fullResponse: response.data
      });
      
      // Update hasMore state based on pagination metadata
      setHasMoreMessages(response.data.pagination?.has_more || false);
      
      // Ensure we have the expected response structure
      if (!response.data || !Array.isArray(response.data.messages)) {
        console.error('❌ Invalid API response structure:', response.data);
        throw new Error('Invalid API response structure');
      }

      const threadMessages = (response.data.messages || []).map((msg: any) => ({
        id: msg._id || msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: typeof msg.timestamp === 'number' ? new Date(msg.timestamp * 1000) : new Date(msg.timestamp),
        images: msg.images || msg.image_urls || [],  // Backward compatibility
        imageIds: msg.image_ids || [],  // New field for image IDs
        agentLogs: msg.agent_logs || [],
        toolExecutions: msg.tool_executions || [],
        isThinking: msg.is_thinking || false,
        thinkingMessage: msg.thinking_message || '',
        // Attachment data
        fileIds: msg.file_ids || [],
        artifactIds: msg.artifact_ids || [],
        fileNames: msg.file_names || [],
        artifactNames: msg.artifact_names || [],
        attachments: msg.attachments || [],  // Unified attachments array with source information
        tokenUsage: msg.token_usage ? {
          inputTokens: msg.token_usage.input_tokens || 0,
          outputTokens: msg.token_usage.output_tokens || 0,
          cacheCreationInputTokens: msg.token_usage.cache_creation_input_tokens || 0,
          cacheReadInputTokens: msg.token_usage.cache_read_input_tokens || 0,
          serviceTier: msg.token_usage.service_tier || 'standard',
          costs: msg.token_usage.costs
        } : undefined,
        // Development timing data
        developmentTiming: msg.development_timing,
        // Elaboration-specific fields - keep snake_case for database compatibility
        messageType: msg.message_type,
        elaborationSessionId: msg.elaboration_session_id,
        iterationCount: msg.iteration_count,
        isElaborationMessage: msg.is_elaboration_message || false,
        user_info: msg.user_info,
      }));
      
      console.log('📥 LOAD_MESSAGES: Loaded', threadMessages.length, 'messages from server');
      console.log('📊 ELABORATION SESSION STATUS from backend:', response.data.elaboration_session_status);
      
      // Debug: Log all messages with their elaboration fields
      threadMessages.forEach((msg: Message, index: number) => {
        if (msg.role === 'assistant') {
          console.log(`🔍 Message ${index}:`, {
            id: msg.id,
            role: msg.role,
            messageType: msg.messageType,
            elaborationSessionId: msg.elaborationSessionId,
            iterationCount: msg.iterationCount,
            isElaborationMessage: msg.isElaborationMessage,
            contentPreview: msg.content?.substring(0, 100) + '...'
          });
        }
      });
      
      // Handle pagination: if beforeMessageId is provided, prepend to existing messages
      if (beforeMessageId && messages.length > 0) {
        // This is a pagination request - prepend older messages
        const combinedMessages = [...threadMessages, ...messages];
        setMessages(combinedMessages);
        onMessageUpdate?.(combinedMessages);
      } else {
        // This is an initial load or refresh - replace all messages
        setMessages(threadMessages);
        onMessageUpdate?.(threadMessages);
      }
      
      // Check if there's an active elaboration session to restore
      const elaborationMessages = threadMessages.filter((msg: Message) =>
        msg.messageType === 'ELABORATION' ||
        msg.isElaborationMessage ||
        (msg.role === 'assistant' && msg.content?.includes('## Overall Summary') && msg.content?.includes('## Sub Tasks'))
      );

      if (elaborationMessages.length > 0) {
        // Get the most recent elaboration message
        const latestElaboration = elaborationMessages[elaborationMessages.length - 1];
        const elaborationIndex = threadMessages.findIndex((msg: Message) => msg.id === latestElaboration.id);

        // Check if there are any development messages after the latest elaboration
        const messagesAfterElaboration = threadMessages.slice(elaborationIndex + 1);
        const hasDevelopmentAfter = messagesAfterElaboration.some((msg: Message) =>
          msg.role === 'assistant' &&
          msg.messageType !== 'ELABORATION' &&
          !msg.isThinking &&
          msg.content &&
          msg.content.trim().length > 0
        );

        if (!hasDevelopmentAfter) {
          // Elaboration is still active - check backend session status
          const sessionId = latestElaboration.elaborationSessionId || `fallback_${latestElaboration.id}`;
          const backendSessionStatus = response.data.elaboration_session_status?.[sessionId];

          // Check if elaboration was cancelled or stopped in backend
          if (backendSessionStatus === 'CANCELLED' || backendSessionStatus === 'STOPPED') {
            // Mark as cancelled locally and disable elaboration
            setCancelledElaborations(prev => new Set([...Array.from(prev), sessionId]));
            setIsElaborationActive(false);
            setElaborationSession(null);
          } else if (cancelledElaborations.has(sessionId)) {
            // Already cancelled locally - ensure elaboration is disabled
            setIsElaborationActive(false);
            setElaborationSession(null);
          } else {
            // Only restore if backend says session is ACTIVE or WAITING_FOR_USER
            const shouldRestore = (backendSessionStatus === 'ACTIVE' || backendSessionStatus === 'WAITING_FOR_USER');

            if (shouldRestore) {
              setIsElaborationActive(true);
              setElaborationSession({
                session_id: sessionId,
                current_elaboration: latestElaboration.content || '',
                status: 'active',
                iteration_count: latestElaboration.iterationCount || 1
              });
            } else {
              setIsElaborationActive(false);
              setElaborationSession(null);
            }
          }
        } else {
          setIsElaborationActive(false);
          setElaborationSession(null);
        }
      }
      
      if (threadMessages.length > 0) {
        const lastMessage = threadMessages[threadMessages.length - 1];
        setLastMessageTimestamp(lastMessage.timestamp.getTime());
      }
    } catch (error: any) {
      console.error('❌ LOAD_MESSAGES ERROR:', error);
      
      // Check if it's a 404 (thread not found) vs other errors
      if (error?.response?.status === 404) {
        console.log('📭 Thread not found (normal for new threads)');
        setMessages([]); // Empty messages for new threads
      } else if (error?.response?.status === 500) {
        console.error('🔥 Server error loading messages:', error?.response?.data);
        setMessages([]); // Don't crash the UI, but log the error
      } else {
        console.error('🔥 Unknown error loading messages:', error);
        setMessages([]); // Don't crash the UI, but log the error
      }
    } finally {
      isLoadingInitialMessagesRef.current = false;
    }
  }, [onMessageUpdate]);

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(async (threadId: string) => {
    if (!messages.length) return;
    
    const oldestMessage = messages[0];
    
    try {
      await loadMessages(threadId, true, 10, oldestMessage.id);
    } catch (error) {
      console.error('❌ Error loading more messages:', error);
    }
  }, [messages, loadMessages]);

  // Add waiting message to the message list - use same ID as updateThinkingMessage
  const addWaitingMessage = useCallback(() => {
    const waitingMessage: Message = {
      id: 'agent-thinking',
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isThinking: true,
      thinkingMessage: 'Working on your request...'
    };
    
    setMessages(prev => {
      // Remove existing thinking message if any
      const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
      return [...filtered, waitingMessage];
    });
  }, []);

  // Remove waiting message from the message list - use same ID as updateThinkingMessage
  const removeWaitingMessage = useCallback(() => {
    setMessages(prev => prev.filter(msg => msg.id !== 'agent-thinking'));
  }, []);

  // Check for incomplete conversations and set up waiting state/timer
  const checkIncompleteConversation = useCallback(async (threadId: string, skipMessageLoad = false) => {
    console.log(`🔍 Checking for incomplete conversation on thread: ${threadId}`);
    console.log(`📋 Current state - waitingState: ${waitingState}, isLoading: ${isLoading}, isStreaming: ${isStreaming}`);
    
    // Clear any existing timers
    if (waitingTimeoutRef.current) {
      console.log('🧹 Clearing existing timer before new check');
      window.clearTimeout(waitingTimeoutRef.current);
      waitingTimeoutRef.current = null;
    }
    if (messageTimeoutRef.current) {
      window.clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = null;
    }
    
    setIsCheckingState(true);
    
    try {
      // Check conversation state and thread status
      console.log(`🌐 Making API calls to check conversation state for thread: ${threadId}`);
      const { conversationState, threadStatus } = await checkConversationState(threadId);
      
      console.log('🔍 API Response - Conversation state:', conversationState);
      console.log('🔍 API Response - Thread status:', threadStatus);
      
      // Store the conversation state for UI usage
      setCurrentConversationState(conversationState);
      
      // Check for all active statuses for multi-window sync support
      // Active statuses: IN_PROGRESS, CLASSIFYING, ELABORATING, DEVELOPMENT_IN_PROGRESS
      const activeStatuses = ['IN_PROGRESS', 'CLASSIFYING', 'ELABORATING', 'DEVELOPMENT_IN_PROGRESS'];
      const isActivelyProcessing = conversationState && activeStatuses.includes(conversationState.execution_status);
      
      if (isActivelyProcessing) {
        console.log('⏳ ACTIVE CONVERSATION DETECTED! Process in progress, showing waiting state and starting 60s timer');
        console.log(`📊 Details: execution_status=${conversationState.execution_status}, is_active=${threadStatus?.is_active}`);
        
        if (!skipMessageLoad) {
          console.log('📥 Loading messages for incomplete conversation...');
          await loadMessages(threadId); // Load existing messages first
        } else {
          console.log('📥 SKIPPING message load for incomplete conversation (preserving local messages)');
        }
        
        setWaitingState('waiting_for_updates');
        
        // Set streaming state for in-progress threads (enables Stop button, disables textarea)
        setIsLoading(false);
        setIsStreaming(true);
        
        // Add waiting message to the conversation
        addWaitingMessage();
        
        // Start 60-second timeout for resume/discard popup [[memory:6385815]]
        console.log(`⏰ Starting 60-second timer for thread: ${threadId}`);
        console.log(`📌 Timer set with threadId: ${threadId}, currentThreadId: ${currentThreadId}, currentThreadIdRef: ${currentThreadIdRef.current}`);
        
        // Generate unique timeout ID to prevent race conditions
        const currentTimeoutId = ++timeoutIdRef.current;
        console.log(`🔢 Timeout ID: ${currentTimeoutId}`);
        
        // Capture the thread ID at timer creation to avoid stale closure issues
        const timerThreadId = threadId;
        waitingTimeoutRef.current = window.setTimeout(() => {
          console.log(`⏰ 60-second timeout reached for timer thread: ${timerThreadId}, timeout ID: ${currentTimeoutId}`);
          
          // Check if this timeout is still valid (not superseded by a newer one)
          if (timeoutIdRef.current !== currentTimeoutId) {
            console.log(`❌ Timeout ${currentTimeoutId} is stale (current: ${timeoutIdRef.current}), ignoring`);
            return;
          }
          
          // Get the FRESH current thread ID from the ref (not stale closure)
          const freshCurrentThreadId = currentThreadIdRef.current;
          console.log(`🎯 Fresh comparison - Timer thread: ${timerThreadId}, Current thread: ${freshCurrentThreadId}`);
          
          // Only show popup if user is still on the same thread
          if (freshCurrentThreadId === timerThreadId) {
            console.log('✅ User still on same thread, showing resume/discard popup');
            setWaitingState('show_resume_discard');
          } else {
            console.log('❌ User switched threads, not showing popup (was on thread:', timerThreadId, ', now on:', freshCurrentThreadId, ')');
          }
        }, 60000); // 60 seconds
        
        console.log('✅ Timer set successfully, returning true for incomplete conversation');
        return true; // Indicates incomplete conversation was found
      }
      
      // No incomplete conversation found
      console.log('✅ No incomplete conversation found - thread is complete or not active');
      console.log(`📊 Details: execution_status=${conversationState?.execution_status}, is_active=${threadStatus?.is_active}`);
      if (!skipMessageLoad) {
        await loadMessages(threadId);
      } else {
        console.log('📥 SKIPPING message load for completed conversation (preserving local messages)');
      }
      setWaitingState('none');
      return false;
      
    } catch (error) {
      console.error('❌ Error checking incomplete conversation (proceeding normally):', error);
      // Fall back to loading messages normally
      if (!skipMessageLoad) {
        await loadMessages(threadId);
      }
      setWaitingState('none');
      return false;
    } finally {
      setIsCheckingState(false);
    }
  }, [checkConversationState, loadMessages, addWaitingMessage]);

  // Handle reconnection flow
  const handleReconnection = useCallback(async (threadId: string) => {
    console.log(`🔄 Handling reconnection for thread: ${threadId}`);
    
    // Clear any existing timers and state for clean slate
    if (waitingTimeoutRef.current) {
      console.log('🧹 Clearing existing timeout before checking incomplete conversation');
      window.clearTimeout(waitingTimeoutRef.current);
      waitingTimeoutRef.current = null;
    }
    
    console.log(`🔍 About to check incomplete conversation for thread: ${threadId}`);
    
    setWaitingState('none');
    setIsLoading(false);
    setIsStreaming(false);
    
    await checkIncompleteConversation(threadId, false);
  }, [checkIncompleteConversation]);

  // Resume conversation (UI state management only - API call handled by dialog)
  const resumeConversation = useCallback(async (threadId: string): Promise<boolean> => {
    try {
      console.log(`🔄 Processing resume for thread: ${threadId} (API call handled by dialog)`);
      
      // Clear waiting state and start listening for updates via WebSocket
      console.log('📡 Resume initiated, setting state to waiting_for_updates (WebSocket-based)');
      setWaitingState('waiting_for_updates');
      setLastMessageTimestamp(Date.now());
      
      // Add agent thinking message for progress handling
      const thinkingMessage: Message = {
        id: 'agent-thinking',
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isThinking: true,
        thinkingMessage: 'Working on your request...'
      };
      
      setMessages(prev => {
        // Remove existing waiting and thinking messages if any
        const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
        return [...filtered, thinkingMessage];
      });
      
      // Set loading state to indicate we're expecting WebSocket updates
      console.log('🔄 Setting loading=true, streaming=false - expecting WebSocket updates');
      setIsLoading(true);
      setIsStreaming(false);
      
      // Reset the 60-second timer
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
      }
      
      // Generate unique timeout ID to prevent race conditions
      const currentTimeoutId = ++timeoutIdRef.current;
      console.log(`🔢 Resume timeout ID: ${currentTimeoutId}`);
      
      waitingTimeoutRef.current = window.setTimeout(() => {
        console.log(`⏰ Resume timeout reached, checking if still on same thread... (timeout ID: ${currentTimeoutId})`);
        
        // Check if this timeout is still valid (not superseded by a newer one)
        if (timeoutIdRef.current !== currentTimeoutId) {
          console.log(`❌ Resume timeout ${currentTimeoutId} is stale (current: ${timeoutIdRef.current}), ignoring`);
          return;
        }
        
        // Get fresh current thread ID from ref
        const freshCurrentThreadId = currentThreadIdRef.current;
        console.log(`🎯 Resume timeout - Timer thread: ${threadId}, Current thread: ${freshCurrentThreadId}`);
        
        // Only show popup if user is still on the same thread
        if (freshCurrentThreadId === threadId) {
          console.log('✅ User still on same thread, showing resume/discard popup');
          setWaitingState('show_resume_discard');
        } else {
          console.log('❌ User switched threads, not showing popup (was on thread:', threadId, ', now on:', freshCurrentThreadId, ')');
        }
      }, 60000);
      
      console.log('✅ Resume processing completed - waiting for WebSocket updates');
      return true;
    } catch (error) {
      console.error('Failed to process resume:', error);
      return false;
    }
  }, []);

  // Discard conversation (API call handled by dialog component)
  const discardConversation = useCallback(async (threadId: string): Promise<boolean> => {
    try {
      console.log(`🗑️ Processing discard for thread: ${threadId} (API call handled externally)`);
      
      // Remove waiting message and clear waiting state and timers
      removeWaitingMessage();
      setWaitingState('none');
      setIsLoading(false);
      setIsStreaming(false);
      
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
      if (messageTimeoutRef.current) {
        window.clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = null;
      }
      
      // Reload conversation to get final state after discard
      await loadMessages(threadId, true); // Force reload after discard
      
      console.log('✅ Conversation discard processed successfully and reloaded');
      return true;
    } catch (error) {
      console.error('Failed to process discard conversation:', error);
      return false;
    }
  }, [removeWaitingMessage, loadMessages]);

  // Resume the 60-second timeout (used after credential capture completes)
  const resumeWaitingTimeout = useCallback(() => {
    if (!currentThreadId) return;
    
    console.log('▶️ Resuming 60-second timer after credential capture');
    
    // Clear any existing timeout
    if (waitingTimeoutRef.current) {
      window.clearTimeout(waitingTimeoutRef.current);
    }
    
    // Generate unique timeout ID to prevent race conditions
    const currentTimeoutId = ++timeoutIdRef.current;
    console.log(`🔢 Resumed timeout ID: ${currentTimeoutId}`);
    
    // Capture the thread ID at timer creation
    const timerThreadId = currentThreadId;
    waitingTimeoutRef.current = window.setTimeout(() => {
      console.log(`⏰ Resumed timeout reached for thread: ${timerThreadId}, timeout ID: ${currentTimeoutId}`);
      
      // Check if this timeout is still valid
      if (timeoutIdRef.current !== currentTimeoutId) {
        console.log(`❌ Resumed timeout ${currentTimeoutId} is stale (current: ${timeoutIdRef.current}), ignoring`);
        return;
      }
      
      // Get fresh current thread ID
      const freshCurrentThreadId = currentThreadIdRef.current;
      console.log(`🎯 Fresh comparison after resume - Timer thread: ${timerThreadId}, Current thread: ${freshCurrentThreadId}`);
      
      if (freshCurrentThreadId === timerThreadId) {
        console.log('✅ User still on same thread after resume, showing resume/discard popup');
        setWaitingState('show_resume_discard');
      } else {
        console.log('❌ User switched threads after resume, not showing popup (was on thread:', timerThreadId, ', now on:', freshCurrentThreadId, ')');
      }
    }, 60000);
  }, [currentThreadId]);

  // Wait for more time (extend timeout)
  const waitMore = useCallback(() => {
    console.log('⏰ Extending wait time for 60 more seconds');
    
    setWaitingState('waiting_for_updates');
    
    // Capture current thread ID for timeout check
    const waitingThreadId = currentThreadId;
    
    // Reset the 60-second timer
    if (waitingTimeoutRef.current) {
      window.clearTimeout(waitingTimeoutRef.current);
    }
    
    // Generate unique timeout ID to prevent race conditions
    const currentTimeoutId = ++timeoutIdRef.current;
    console.log(`🔢 Wait more timeout ID: ${currentTimeoutId}`);
    
    waitingTimeoutRef.current = window.setTimeout(() => {
      console.log(`⏰ Wait more timeout reached, checking if still on same thread... (timeout ID: ${currentTimeoutId})`);
      
      // Check if this timeout is still valid (not superseded by a newer one)
      if (timeoutIdRef.current !== currentTimeoutId) {
        console.log(`❌ Wait more timeout ${currentTimeoutId} is stale (current: ${timeoutIdRef.current}), ignoring`);
        return;
      }
      
      // Get fresh current thread ID from ref
      const freshCurrentThreadId = currentThreadIdRef.current;
      console.log(`🎯 Wait more timeout - Timer thread: ${waitingThreadId}, Current thread: ${freshCurrentThreadId}`);
      
      // Only show popup if user is still on the same thread
      if (freshCurrentThreadId === waitingThreadId) {
        console.log('✅ User still on same thread, showing resume/discard popup');
        setWaitingState('show_resume_discard');
      } else {
        console.log('❌ User switched threads, not showing popup (was on thread:', waitingThreadId, ', now on:', freshCurrentThreadId, ')');
      }
    }, 60000);
  }, [currentThreadId]);

  // Credential submission function
  const submitCapturedCredentials = useCallback(async (requestId: string, values: Record<string, string>) => {
    try {
      // Check if user skipped providing credentials
      const userSkipped = values['__skipped_by_user'] === 'true';
      
      const response = await api.post(`/dev-agent/projects/${projectId}/credentials/submit`, {
        request_id: requestId,
        captured_values: values
      });
      
      // Show appropriate message based on whether user skipped or provided credentials
      if (userSkipped) {
        addProcessingMessage('⏭️ Credentials skipped - proceeding without them');
      } else {
        addProcessingMessage('✅ Credentials submitted successfully');
      }
      
      // Clear the credential capture request after successful submission
      setCredentialCaptureRequest(null);
      
      // Resume the 60-second timeout since user interaction is complete
      resumeWaitingTimeout();
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to submit credentials:', error);
      
      // Check if this was a skip attempt or regular submission for error message
      const userSkipped = values['__skipped_by_user'] === 'true';
      if (userSkipped) {
        addProcessingMessage('Failed to skip credentials');
      } else {
        addProcessingMessage('Failed to submit credentials');
      }
      
      // Even on error, resume the timeout since user tried to submit
      resumeWaitingTimeout();
      
      throw error;
    }
  }, [projectId, resumeWaitingTimeout]);

  // Check for pending credential requests on thread selection (for state persistence)
  const checkForPendingCredentialRequest = useCallback(async (threadId: string) => {
    try {
      const response = await api.get(`/dev-agent/projects/${projectId}/threads/${threadId}/pending-credential-request`);
      if (response.data.has_pending_request) {
        const requestData = response.data.request_data;
        setCredentialCaptureRequest({
          requestId: requestData.request_id,
          parameters: requestData.parameters,
          envFilePath: requestData.env_file_path
        });
        console.log('🔄 Restored pending credential request:', requestData.request_id);
      }
    } catch (error) {
      console.error('❌ Failed to check for pending credential request:', error);
    }
  }, [projectId]);

  // Select thread (handles reconnection logic)
  const selectThread = useCallback(async (threadId: string | undefined) => {
    console.log(`🎯 selectThread called with: ${threadId} (current: ${currentThreadId})`);
    console.log('🚀 [DEV_MSG_FLOW] selectThread - current messages before:', messages.length);

    // Disconnect from previous thread
    if (currentThreadId && currentThreadId !== threadId) {
      console.log(`🔌 Disconnecting from previous thread: ${currentThreadId}`);
      webSocket.disconnect();
    }

    console.log(`📝 Setting currentThreadId to: ${threadId}`);
    setCurrentThreadId(threadId);
    
    // Immediately update the ref to avoid timing issues with async state updates
    currentThreadIdRef.current = threadId;
    
    if (!threadId) {
      setMessages([]);
      setWaitingState('none');
      setIsLoading(false);
      setIsStreaming(false);

      // Clear any active timeouts
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
      if (messageTimeoutRef.current) {
        window.clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = null;
      }

      return;
    }

    // Handle reconnection flow (now robust for both new and existing threads)
    await handleReconnection(threadId);

    // Connect to WebSocket
    try {
      await webSocket.connectToThread(threadId, {
        onReconnect: async () => {
          // Check for incomplete conversations on reconnection (skip message reload since we already have them)
          await checkIncompleteConversation(threadId, true);
        },
        onMessage: (message) => {
          // Handle dev_phase_update for multi-window sync
          if (message.type === 'dev_phase_update') {
            // Update processing message to show current phase progress
            setMessages(prev => {
              const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
              const progressMessage: Message = {
                id: 'agent-thinking',
                role: 'assistant',
                content: '',
                timestamp: new Date(),
                isThinking: true,
                thinkingMessage: message.data.message || 'Processing...'
              };
              return [...filtered, progressMessage];
            });
          }

          // Update last message timestamp when receiving updates
          setLastMessageTimestamp(Date.now());

          // If we were waiting for updates and received a message, clear waiting state
          setWaitingState(currentWaitingState => {
            if (currentWaitingState === 'waiting_for_updates') {
              return 'none';
            }
            return currentWaitingState;
          });

          // Clear the 60-second timeout for any message indicating active processing
          // This prevents false resume/discard popups when backend is actively working
          // Support both old and new event names for backwards compatibility
          const activeProcessingMessageTypes = [
            'dev_text_start', 'dev_tool_start', 'dev_tool_progress', 'dev_tool_result',
            'dev_file_change', 'dev_message_start', 'dev_message_streaming', 'dev_message_update',
            'dev_status', 'dev_tool_execution', 'dev_agent_log', 'dev_message_complete', 'dev_token_usage_complete',
            'dev_phase_update', 'dev_elaboration_approved', 'dev_processing_status'
          ];

          if (activeProcessingMessageTypes.includes(message.type)) {
            if (waitingTimeoutRef.current) {
              window.clearTimeout(waitingTimeoutRef.current);
              waitingTimeoutRef.current = null;
            }
          }

          // Handle dev_processing_status for multi-window sync (initial state on connect)
          if (message.type === 'dev_processing_status') {
            console.log('📡 [DEV] Processing status on connect:', message.data);
            
            if (message.data.is_processing) {
              setIsLoading(true);
              setIsStreaming(true);
              // Set elaboration state if applicable
              if (message.data.phase === 'elaboration' && message.data.session_id) {
                setIsElaborationActive(true);
                setElaborationSession({
                  session_id: message.data.session_id,
                  current_elaboration: '',
                  status: 'active',
                  iteration_count: 1
                });
              }
              
              // Show thinking indicator if not already present
              setMessages(prev => {
                const hasThinking = prev.some(m => m.isThinking);
                if (hasThinking) return prev;
                
                const thinkingMessage: Message = {
                  id: 'agent-thinking',
                  role: 'assistant',
                  content: '',
                  timestamp: new Date(),
                  isThinking: true,
                  thinkingMessage: message.data.message || 'Processing...'
                };
                return [...prev, thinkingMessage];
              });
            }
            return; // Early return for processing status
          }

          // Handle different message types - dev_message for Dev Mode multi-window sync
          if (message.type === 'dev_message') {
            setMessages(prev => {
              // For user messages, check if we already have a message with the same content
              // (optimistic update from this window). If so, just skip to avoid duplicate.
              if (message.data.role === 'user') {
                const lastUserMessage = [...prev].reverse().find(m => m.role === 'user');
                if (lastUserMessage && lastUserMessage.content === message.data.content) {
                  return prev;
                }
              }

              // Use camelCase fields directly from backend
              const mappedMessage = {
                ...message.data,
                messageType: message.data.messageType,
                elaborationSessionId: message.data.elaborationSessionId,
                iterationCount: message.data.iterationCount,
                isElaborationMessage: message.data.isElaborationMessage || false
              };

              // Check if this is an elaboration message and set active state
              if (mappedMessage.messageType === 'ELABORATION' || mappedMessage.isElaborationMessage) {
                setIsElaborationActive(true);
                setElaborationSession({
                  session_id: mappedMessage.elaborationSessionId || `fallback_${mappedMessage.id}`,
                  current_elaboration: mappedMessage.content || '',
                  status: 'active',
                  iteration_count: mappedMessage.iterationCount || 1
                });
              }

              // Remove thinking message and add new message
              const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
              const updated = [...filtered, mappedMessage];

              onMessageUpdate?.(updated);
              return updated;
            });
            setIsLoading(false);
            setIsStreaming(false);
          } else if (message.type === 'dev_message_update' || message.type === 'dev_message_updated') {
            setMessages(prev => {
              const updated = prev.map(msg => {
                if (msg.id === message.data.id) {
                  // Update existing message with new content and metadata
                  return {
                    ...msg,
                    content: message.data.content,
                    timestamp: message.data.timestamp ? new Date(message.data.timestamp) : msg.timestamp,
                    iterationCount: message.data.iterationCount || msg.iterationCount,
                    isUpdated: true,
                    updatedAt: new Date()
                  };
                }
                return msg;
              });
              onMessageUpdate?.(updated);
              return updated;
            });
          } else if (message.type === 'dev_phase_update') {
            // Handle enhanced workflow phase updates (classification, elaboration, development)
            const phaseMessage = message.data?.message || 'Processing your request...';
            const phaseStatus = message.data?.status;
            console.log(`🔄 DEV_PHASE_UPDATE: phase=${message.data?.phase}, status=${phaseStatus}, message="${phaseMessage}"`);

            // Handle error status immediately
            if (phaseStatus === 'error') {
              console.error('❌ Phase error received:', phaseMessage);

              // Clear loading/streaming states
              setIsLoading(false);
              setIsStreaming(false);
              setIsElaborationActive(false);
              setElaborationSession(null);

              // Remove thinking message and show error message
              setMessages(prev => {
                // Remove thinking message
                const filtered = prev.filter(msg => msg.id !== 'agent-thinking' && !msg.isThinking);

                // Add error message
                const errorMessage: Message = {
                  id: generateMessageId('phase-error'),
                  role: 'assistant',
                  content: phaseMessage,
                  timestamp: new Date(),
                  isThinking: false
                };

                const updated = [...filtered, errorMessage];
                onMessageUpdate?.(updated);
                return updated;
              });

              return; // Early return to prevent further processing
            }

            console.log('🚀 [DEV_MSG_FLOW] dev_phase_update - about to update thinking message');
            updateThinkingMessage(phaseMessage);
            console.log('🚀 [DEV_MSG_FLOW] dev_phase_update - thinking message updated');
            setIsLoading(true);
            
            // Handle phase-specific UI updates for multi-window sync
            if (message.data?.phase === 'classification' && message.data?.status === 'started') {
              console.log('🔄 SETTING isElaborationActive = TRUE (classification started)');
              setIsElaborationActive(true);
              setIsStreaming(true);
              setElaborationSession({
                session_id: `classification_${currentThreadId}`,
                current_elaboration: '',
                status: 'active',
                iteration_count: 0
              });
            } else if (message.data?.phase === 'classification' && message.data?.status === 'completed') {
              // Keep elaboration active as we move to elaboration phase
              console.log('🔄 Classification completed, continuing to elaboration phase');
            } else if (message.data?.phase === 'development') {
              // Development phase started - clear elaboration state and show development progress
              console.log('🚀 Development phase started - clearing elaboration state for multi-window sync');
              setIsElaborationActive(false);
              setElaborationSession(null);
              setIsStreaming(true);
            }
          } else if (message.type === 'dev_agent_thinking' || message.type === 'dev_agent_start') {
            // Show backend message or fallback to generic message for basic agent states
            const agentMessage = (message.data && message.data.message) ? message.data.message : 'Working on your request...';
            console.log(`🤖 Updating thinking message: "${agentMessage}"`);
            updateThinkingMessage(agentMessage);
            setIsLoading(true);
          } else if (message.type === 'dev_tool_start') {
            // Handle tool start - add to thinking message's tool executions
            console.log(`🔧 DEV_TOOL_START: ${message.data.tool_name}`, message.data);
            
            const friendlyMessage = getFriendlyEventMessage(message);
            console.log(`🔥🔥🔥 TOOL_START UPDATE: "${friendlyMessage}"`);
            
            // Update thinking message with tool execution
            setMessages(prev => {
              const thinkingMsg = prev.find(msg => msg.id === 'agent-thinking');
              
              if (thinkingMsg) {
                // Add new tool execution to the thinking message
                const newToolExecution = {
                  id: message.data.tool_id,
                  tool_name: message.data.tool_name,
                  status: 'running',
                  friendly_message: message.data.friendly_message,
                  args: message.data.args
                };
                
                const updatedToolExecutions = [...(thinkingMsg.toolExecutions || []), newToolExecution];
                
                return prev.map(msg => 
                  msg.id === 'agent-thinking'
                    ? { ...msg, toolExecutions: updatedToolExecutions, thinkingMessage: friendlyMessage }
                    : msg
                );
              } else {
                // Create thinking message with tool execution
                const thinkingMessage: Message = {
                  id: 'agent-thinking',
                  role: 'assistant',
                  content: '',
                  timestamp: new Date(),
                  isThinking: true,
                  thinkingMessage: friendlyMessage,
                  toolExecutions: [{
                    id: message.data.tool_id,
                    tool_name: message.data.tool_name,
                    status: 'running',
                    friendly_message: message.data.friendly_message,
                    args: message.data.args
                  }]
                };
                return [...prev, thinkingMessage];
              }
            });
            
            setIsLoading(true);
          } else if (message.type === 'dev_tool_result') {
            // Handle tool completion - update the tool execution status
            console.log(`✅ DEV_TOOL_RESULT: ${message.data.tool_name}`, message.data);
            
            const friendlyMessage = getFriendlyEventMessage(message);
            console.log(`🔥🔥🔥 TOOL_RESULT UPDATE: "${friendlyMessage}"`);
            
            // Update the tool execution in thinking message
            setMessages(prev => {
              const thinkingMsg = prev.find(msg => msg.id === 'agent-thinking');
              
              if (thinkingMsg && thinkingMsg.toolExecutions) {
                const updatedToolExecutions = thinkingMsg.toolExecutions.map(tool =>
                  tool.id === message.data.tool_id
                    ? {
                        ...tool,
                        status: message.data.status || 'completed',
                        result: message.data.result,
                        duration: message.data.duration,
                        files_modified: message.data.files_modified,
                        friendly_message: message.data.friendly_message
                      }
                    : tool
                );
                
                return prev.map(msg =>
                  msg.id === 'agent-thinking'
                    ? { ...msg, toolExecutions: updatedToolExecutions, thinkingMessage: friendlyMessage }
                    : msg
                );
              }
              
              return prev;
            });
            
            setIsLoading(true);
          } else if (message.type === 'dev_status' || message.type === 'dev_tool_progress') {
            // Display backend StreamingEvent messages as BlueGenAI Agent updates
            const friendlyMessage = getFriendlyEventMessage(message);
            console.log(`🔥🔥🔥 PROGRESS UPDATE: ${message.type} -> "${friendlyMessage}"`);
            
            // Update with backend message (updateThinkingMessage will ignore blanks)
            updateThinkingMessage(friendlyMessage);
            setIsLoading(true);
          } else if (message.type === 'dev_message_start') {
            // Just create assistant message, keep thinking message for ongoing tool events  
            console.log('🔥🔥🔥 MESSAGE_START RECEIVED');
            setMessages(prev => {
              const assistantMessage = {
                id: generateMessageId('assistant', message.data?.message_id),
                role: 'assistant' as const,
                content: '',
                timestamp: new Date(),
                isThinking: false
              };
              const updated = [...prev, assistantMessage];
              console.log('🔥🔥🔥 Created assistant message placeholder');
              onMessageUpdate?.(updated);
              return updated;
            });
            setIsLoading(false);
            setIsStreaming(true);
          } else if (message.type === 'dev_text_start') {
            // Text streaming is starting
            setIsStreaming(true);
          // Removed iteration_response handling - not needed
          // Tool progress is already shown via dev_tool_start/progress/result events
          } else if (message.type === 'dev_agent_log' || message.type === 'dev_tool_execution') {
            // Ignore technical details - keep showing simple friendly message
            // (No action needed, just keep the existing friendly "Working on your request..." message)
          } else if (message.type === 'dev_message_streaming') {
            // Handle legacy streaming updates
            setIsStreaming(true);
            setMessages(prev => {
              const updated = [...prev];
              // Find the last assistant message and update it
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === 'assistant' && updated[i].id !== 'agent-thinking') {
                  updated[i] = { ...updated[i], content: message.data.content || '' };
                  break;
                }
              }
              onMessageUpdate?.(updated);
              return updated;
            });
          } else if (message.type === 'dev_message_complete') {
            // Complete response received - create final assistant message
            console.log('🔥🔥🔥 DEV_MESSAGE_COMPLETE RECEIVED!');
            console.log('🔥🔥🔥 Raw message data:', JSON.stringify(message.data, null, 2));
            
            const finalContent = message.data?.content || message.data?.message || '';
            console.log('🔥🔥🔥 Extracted final content:', finalContent);
            
            if (!finalContent || finalContent.trim() === '') {
              console.error('❌ No valid content in message_complete event!');
              console.error('❌ Message data:', message.data);
              // Use fallback content
              const fallbackContent = 'Response completed but no content received.';
              console.log('🔧 Using fallback content:', fallbackContent);
            }

            // Check if message contains URLs and trigger preview panel
            const urlPattern = /https?:\/\/[^\s]+/g;
            const hasUrls = finalContent && urlPattern.test(finalContent);
            console.log('🌐 [useDevAgent] Message has URLs:', hasUrls);
            if (hasUrls) {
              console.log('✅ [useDevAgent] Triggering preview panel callback');
              onNewMessageWithUrls?.();
            }
            
            setMessages(prev => {
              console.log('🔥🔥🔥 MESSAGE_COMPLETE: Processing final response, current messages:', prev.length);
              const userMessages = prev.filter(m => m.role === 'user');
              console.log('🔥🔥🔥 MESSAGE_COMPLETE: User messages before update:', userMessages.length);
              
              // Get tool executions from thinking message before removing it
              const thinkingMsg = prev.find(msg => msg.id === 'agent-thinking');
              const toolExecutions = thinkingMsg?.toolExecutions || [];
              console.log('🔥🔥🔥 MESSAGE_COMPLETE: Transferring', toolExecutions.length, 'tool executions to final message');
              
              // Remove all thinking messages 
              const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
              
              // Find the existing assistant message created by dev_message_start and UPDATE it
              const existingAssistantMsg = [...filtered].reverse().find(msg =>
                msg.role === 'assistant' && msg.id.includes('assistant')
              );
              let updated;
              if (existingAssistantMsg) {
                updated = filtered.map(msg =>
                  msg.id === existingAssistantMsg.id
                    ? {
                        ...msg,
                        content: finalContent || 'Response completed but no content received.',
                        toolExecutions: toolExecutions.length > 0 ? toolExecutions : undefined,
                        isThinking: false
                      }
                    : msg
                );
              } else {
                const assistantMessage = {
                  id: generateMessageId('assistant-complete', message.data?.message_id),
                  role: 'assistant' as const,
                  content: finalContent || 'Response completed but no content received.',
                  timestamp: new Date(),
                  isThinking: false,
                  toolExecutions: toolExecutions.length > 0 ? toolExecutions : undefined
                };
                updated = [...filtered, assistantMessage];
              };
              
              const userMessagesAfter = updated.filter(m => m.role === 'user');
              console.log('🔥🔥🔥 MESSAGE_COMPLETE: User messages after update:', userMessagesAfter.length);
              console.log('🔥🔥🔥 MESSAGE_COMPLETE: Added final response, total messages:', updated.length);
              
              onMessageUpdate?.(updated);
              return updated;
            });
            
            // Only stop loading, but keep streaming true until task_complete
            // This ensures the stop button remains visible during development
            setIsLoading(false);
            console.log('🔥🔥🔥 Loading stopped, but keeping streaming for stop button');
          } else if (message.type === 'dev_token_usage_complete') {
            // Handle token usage completion - add token usage to the last assistant message
            console.log('💰 DEV Token usage received:', message.data);
            
            setMessages(prev => {
              const updated = [...prev];
              
              // Find the last assistant message and add token usage
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === 'assistant' && updated[i].id !== 'agent-thinking') {
                  updated[i] = { 
                    ...updated[i], 
                    tokenUsage: {
                      inputTokens: message.data.input_tokens || 0,
                      outputTokens: message.data.output_tokens || 0,
                      cacheCreationInputTokens: message.data.cache_creation_input_tokens || 0,
                      cacheReadInputTokens: message.data.cache_read_input_tokens || 0,
                      serviceTier: message.data.service_tier || 'standard',
                      costs: message.data.costs
                    }
                  };
                  break;
                }
              }
              
              onMessageUpdate?.(updated);
              return updated;
            });
          } else if (message.type === 'dev_refinement_complete') {
            // Handle requirement refinement completion - trigger project stats refresh
            // Note: project_id is inside data for dev_refinement_complete
            const refinementProjectId = message.data?.project_id || projectId;
            
            // Use async function to handle API calls for refinement completion
            const fetchUpdatedStatsAfterRefinement = async () => {
              if (onProjectStatsUpdate && refinementProjectId) {
                try {
                  // Fetch updated stats from API after refinement
                  const response = await api.get(`/projects/${refinementProjectId}/stats`);
                  // Create stats object with just the cost data
                  const statsUpdate = { total_cost: parseFloat(response.data.total_cost) || 0 };
                  onProjectStatsUpdate(refinementProjectId, statsUpdate);
                } catch (err) {
                  console.error('Failed to fetch updated project stats:', err);
                }
              }
            };
            
            // Call the async function
            fetchUpdatedStatsAfterRefinement();
            
          } else if (message.type === 'dev_task_complete' || message.type === 'dev_workflow_complete') {
            // Handle final task completion event
            console.log('✅ Dev Task/Workflow completed:', message.type, message.data);

            // Batch all message updates into a single setMessages call to prevent flicker
            const messageId = message.data?.message_id;
            const developmentTiming = message.data?.development_timing;

            if (messageId || developmentTiming) {
              setMessages(prev => {
                const updated = [...prev];
                // Find the last assistant message and update it with all data at once
                for (let i = updated.length - 1; i >= 0; i--) {
                  if (updated[i].role === 'assistant' && updated[i].id !== 'agent-thinking') {
                    const updates: any = {};
                    if (messageId) {
                      console.log(`📨 Updating message ID from ${updated[i].id} to ${messageId}`);
                      updates.id = messageId;
                    }
                    if (developmentTiming) {
                      console.log('⏱️ Development timing added to message:', messageId || updated[i].id);
                      updates.developmentTiming = developmentTiming;
                    }
                    updated[i] = { ...updated[i], ...updates };
                    break;
                  }
                }
                onMessageUpdate?.(updated);
                return updated;
              });
            }

            // Handle specific workflow statuses for enhanced workflow
            // Note: workflow_complete with 'development_started' status comes AFTER task_complete
            // so we should NOT re-enable the stop button at this point
            if (message.type === 'dev_workflow_complete' && message.data?.status) {
              console.log('🎯 [ENHANCED WORKFLOW] Dev workflow status:', message.data.status);

              if (message.data.status === 'development_started') {
                // This status indicates development WAS started, but workflow_complete means it's now done
                // Don't re-enable stop button here - development is already complete
                console.log('🚀 [ENHANCED WORKFLOW] Direct development completed via WebSocket');

              } else if (message.data.status === 'elaboration_active') {
                console.log('📝 [ENHANCED WORKFLOW] Elaboration active via WebSocket');
                // Elaboration messages will come as separate message events
                // No special UI state changes needed here

              } else if (message.data.status === 'credit_error') {
                console.log('💳 [ENHANCED WORKFLOW] Credit error via WebSocket');
                // Clear ALL loading/active states to re-enable input
                setIsLoading(false);
                setIsStreaming(false);
                setIsElaborationActive(false);
                setElaborationSession(null);
                setWorkflowMode('chat');

                // Add credit error message to the chat
                const creditErrorMessage = {
                  id: generateMessageId('credit-error', message.data?.message_id),
                  role: 'assistant' as const,
                  content: message.data.message || '💳 Insufficient Credits',
                  timestamp: new Date(),
                  isThinking: false,
                  isCreditError: true
                };

                setMessages(prev => {
                  // Remove any thinking messages and add credit error
                  const filtered = prev.filter(msg => !msg.isThinking);
                  const updated = [...filtered, creditErrorMessage];
                  onMessageUpdate?.(updated);
                  return updated;
                });

              } else {
                console.log('ℹ️ [ENHANCED WORKFLOW] Other workflow status:', message.data.status);
              }
            }

            // Only process stats/state updates for dev_task_complete, not dev_workflow_complete
            // This prevents duplicate processing and flicker
            if (message.type === 'dev_task_complete') {
              // Fetch updated project stats after development completion
              // Note: project_id is at top level of message for dev_task_complete, not inside data
              const taskProjectId = message.project_id || projectId;

              // Use async function to handle API calls
              const fetchUpdatedStats = async () => {
                if (onProjectStatsUpdate && taskProjectId) {
                  try {
                    // Fetch updated stats from API
                    const response = await api.get(`/projects/${taskProjectId}/stats`);
                    // Create stats object with just the cost data
                    const statsUpdate = { total_cost: parseFloat(response.data.total_cost) || 0 };
                    onProjectStatsUpdate(taskProjectId, statsUpdate);
                  } catch (err) {
                    console.error('Failed to fetch updated project stats:', err);
                  }
                }
              };

              // Call the async function
              fetchUpdatedStats();

              // Ensure loading/streaming states are stopped when development is truly complete
              setIsLoading(false);
              setIsStreaming(false);
              console.log('🛑 Development completed - stop button now hidden');

              // Reset elaboration state
              console.log('🔄 Task completed - resetting elaboration state');
              setIsElaborationActive(false);
              setElaborationSession(null);
            }
            
            // If this is enhanced workflow completion, fetch and display token summary
            if (workflowMode === 'development' && currentThreadId) {
              console.log('📊 Enhanced workflow completed, fetching token summary...');
              getTokenSummary(currentThreadId).then(summary => {
                if (summary) {
                  console.log('✅ Token summary retrieved, showing display');
                  // Note: We don't set showTokenSummary here in Chat component
                  // Instead, we'll let the Chat component check for tokenSummary state
                }
              }).catch(error => {
                console.error('❌ Failed to fetch token summary:', error);
              });
            }
          } else if (message.type === 'dev_elaboration_approved') {
            // Handle elaboration approval - update UI in passive windows
            // This is sent when another window approves the elaboration
            console.log('✅ DEV ELABORATION APPROVED via WebSocket:', message.data);
            
            const sessionId = message.data?.session_id;
            const elaborationMessageId = message.data?.elaboration_message_id;
            
            // Clear elaboration state (hide the approve/refine buttons)
            setIsElaborationActive(false);
            setElaborationSession(null);
            
            // Show development progress indicator
            setIsLoading(true);
            setIsStreaming(true);
            
            // Update the elaboration message to mark it as approved (remove buttons)
            setMessages(prev => {
              return prev.map(msg => {
                // Find the elaboration message and mark it as approved
                if (msg.elaborationSessionId === sessionId || msg.id === elaborationMessageId) {
                  return {
                    ...msg,
                    isElaborationMessage: false, // Remove elaboration message flag to hide buttons
                    elaborationApproved: true,
                    messageType: 'ELABORATION_FINAL' // Mark as finalized
                  };
                }
                return msg;
              });
            });
            
            // Add thinking message for development progress
            setMessages(prev => {
              const hasThinking = prev.some(m => m.isThinking);
              if (hasThinking) return prev;
              
              const thinkingMessage: Message = {
                id: 'agent-thinking',
                role: 'assistant',
                content: '',
                timestamp: new Date(),
                isThinking: true,
                thinkingMessage: message.data?.message || 'Building your application...'
              };
              return [...prev, thinkingMessage];
            });
            
            console.log('✅ Passive window updated: Elaboration approved, showing development progress');
          } else if (message.type === 'dev_operation_cancelled' || message.type === 'dev_elaboration_stopped') {
            // Handle elaboration cancellation/stopping in real-time (for multi-window sync)
            console.log('❌ DEV OPERATION CANCELLED via WebSocket:', message);
            console.log('❌ ELABORATION CANCELLED message.data:', message.data);
            console.log('❌ ELABORATION CANCELLED data keys:', Object.keys(message.data || {}));
            
            const sessionId = message.data?.session_id || message.data?.elaborationSessionId;
            const tokenUsage = message.data?.token_usage;
            
            console.log('💰 Elaboration cancellation sessionId:', sessionId);
            console.log('💰 Elaboration cancellation tokenUsage:', tokenUsage);
            console.log('💰 Elaboration cancellation tokenUsage type:', typeof tokenUsage);
            console.log('💰 Elaboration cancellation tokenUsage keys:', tokenUsage ? Object.keys(tokenUsage) : 'NO_KEYS');
            
            // Always disable elaboration state (even if no sessionId - for multi-window sync)
            console.log('❌ Clearing elaboration state for multi-window sync');
            setIsElaborationActive(false);
            setElaborationSession(null);
            
            if (sessionId) {
              console.log('❌ Marking elaboration as cancelled:', sessionId);
              // Mark as cancelled locally
              setCancelledElaborations(prev => new Set([...Array.from(prev), sessionId]));
            }
            
            // Update any elaboration messages to remove buttons (for passive windows)
            setMessages(prev => {
              return prev.map(msg => {
                // Find elaboration messages and mark them as cancelled (hide buttons)
                if (msg.isElaborationMessage || msg.messageType === 'ELABORATION' || 
                    (sessionId && msg.elaborationSessionId === sessionId)) {
                  return {
                    ...msg,
                    isElaborationMessage: false, // Remove elaboration message flag to hide buttons
                    elaborationCancelled: true,
                    messageType: 'ELABORATION_CANCELLED'
                  };
                }
                return msg;
              });
            });
            console.log('✅ Updated elaboration messages to hide buttons in passive window');
            
            // Apply token usage to the latest assistant message (cancellation message)
            if (tokenUsage) {
              console.log('💰 Applying token usage to cancellation message:', tokenUsage);
              console.log('💰 tokenUsage.input_tokens:', tokenUsage.input_tokens);
              console.log('💰 tokenUsage.output_tokens:', tokenUsage.output_tokens);
              console.log('💰 tokenUsage.costs:', tokenUsage.costs);
              
              // Function to apply tokens with retry mechanism
              const applyTokensWithRetry = (attempt = 1, maxAttempts = 3) => {
                setMessages(prev => {
                  const updated = [...prev];
                  console.log(`💰 Token application attempt ${attempt}/${maxAttempts} - Current messages:`, updated.length);
                  console.log('💰 Last 3 messages:', updated.slice(-3).map(m => ({id: m.id, role: m.role, hasTokenUsage: !!m.tokenUsage, contentPreview: m.content.substring(0, 30)})));
                  
                  // Find the last assistant message and add token usage
                  let tokenApplied = false;
                  for (let i = updated.length - 1; i >= 0; i--) {
                    if (updated[i].role === 'assistant' && updated[i].id !== 'agent-thinking') {
                      // Check if this looks like a cancellation message
                      const isCancellationMessage = updated[i].content.includes('elaboration cancelled') || 
                                                   updated[i].content.includes('Requirement elaboration cancelled');
                      
                      console.log(`💰 Checking message ${i}: ${updated[i].id}`);
                      console.log(`💰 Content preview: "${updated[i].content.substring(0, 50)}..."`);
                      console.log(`💰 Is cancellation message: ${isCancellationMessage}`);
                      
                      // Apply tokens to the most recent assistant message (preferably cancellation message)
                      if (isCancellationMessage || attempt >= maxAttempts) {
                        console.log(`💰 Applying tokens to message ${i}: ${updated[i].id}`);
                        updated[i] = { 
                          ...updated[i], 
                          tokenUsage: {
                            inputTokens: tokenUsage.input_tokens || 0,
                            outputTokens: tokenUsage.output_tokens || 0,
                            cacheCreationInputTokens: tokenUsage.cache_creation_input_tokens || 0,
                            cacheReadInputTokens: tokenUsage.cache_read_input_tokens || 0,
                            serviceTier: tokenUsage.service_tier || 'standard',
                            costs: tokenUsage.costs
                          }
                        };
                        console.log('💰 Token usage applied to message:', updated[i].id);
                        console.log('💰 Applied tokenUsage:', updated[i].tokenUsage);
                        tokenApplied = true;
                        break;
                      }
                    }
                  }
                  
                  if (!tokenApplied && attempt < maxAttempts) {
                    console.log(`🔄 No cancellation message found yet, retrying in 500ms (attempt ${attempt}/${maxAttempts})`);
                    setTimeout(() => applyTokensWithRetry(attempt + 1, maxAttempts), 500);
                    return prev; // Don't update messages yet
                  }
                  
                  if (!tokenApplied) {
                    console.log('🚫 No assistant message found to apply token usage to after all attempts!');
                  }
                  
                  onMessageUpdate?.(updated);
                  return updated;
                });
              };
              
              // Start token application
              applyTokensWithRetry();
              
            } else {
              console.log('🚫 No token usage to apply - tokenUsage is null/undefined');
            }
            
            // Clear thinking state and loading indicators
            console.log('🧹 Clearing loading states due to cancellation');
            setIsLoading(false);
            setIsStreaming(false);
            // Remove only thinking messages, keep actual stop messages
            setMessages(prev => prev.filter(msg => msg.id !== 'agent-thinking'));
          } else if (message.type === 'dev_credential_capture_request') {
            console.log('🔑 CREDENTIAL_CAPTURE_REQUEST RECEIVED:', message.data);
            
            // Pause the 60-second timeout while waiting for credentials
            if (waitingTimeoutRef.current) {
              console.log('⏸️ Pausing 60-second timer during credential capture');
              window.clearTimeout(waitingTimeoutRef.current);
              waitingTimeoutRef.current = null;
            }
            
            setCredentialCaptureRequest({
              requestId: message.data.request_id,
              parameters: message.data.parameters,
              envFilePath: message.data.env_file_path
            });
            // Keep isLoading true to continue showing "Thinking..." message with tool updates
            // Keep isStreaming true to maintain Stop button and continue receiving WebSocket updates
            console.log('🔑 Credential modal shown - keeping loading/streaming state to show tool updates');
          } else if (message.type === 'dev_credentials_written_to_env') {
            console.log('🔑 CREDENTIALS_WRITTEN_TO_ENV RECEIVED:', message.data);
            setCredentialCaptureRequest(null);
            addProcessingMessage(`✅ Credentials written to ${message.data.file_path} (${message.data.filled_count}/${message.data.total_count} filled)`);
            
            // Resume the 60-second timeout since credential process is complete
            resumeWaitingTimeout();
          } else if (message.type === 'dev_project_stats_update') {
            // Handle real-time project statistics updates
            console.log('📊 PROJECT_STATS_UPDATE RECEIVED:', message.data);
            console.log('📊 Project ID from event:', message.data?.project_id);
            console.log('📊 Token stats from event:', message.data?.token_stats);
            console.log('📊 onProjectStatsUpdate callback available:', !!onProjectStatsUpdate);

            if (onProjectStatsUpdate && message.data?.project_id && message.data?.token_stats) {
              console.log('📊 ✅ Calling onProjectStatsUpdate callback with data:', {
                projectId: message.data.project_id,
                tokenStats: message.data.token_stats
              });
              onProjectStatsUpdate(message.data.project_id, message.data.token_stats);
            } else {
              console.warn('📊 ❌ Cannot call onProjectStatsUpdate callback:', {
                hasCallback: !!onProjectStatsUpdate,
                hasProjectId: !!message.data?.project_id,
                hasTokenStats: !!message.data?.token_stats
              });
            }
          } else if (message.type === 'dev_error') {
            // Handle error messages from backend (Claude API errors, etc.)
            console.error('❌ Dev Error received via WebSocket:', message.data);

            // Clear loading/streaming states
            setIsLoading(false);
            setIsStreaming(false);

            // Extract error message with robust fallbacks
            // Check for error_user_message first (structured error from backend)
            let errorMessage = 'An error occurred. Please try again.';
            if (message.data && message.data.error_user_message && message.data.error_user_message.message) {
              errorMessage = message.data.error_user_message.message;
            } else if (message.data && message.data.message) {
              errorMessage = message.data.message;
            } else if (message.data && typeof message.data === 'string') {
              errorMessage = message.data;
            }

            // Create error message for display as regular assistant message
            const assistantErrorMessage: Message = {
              id: generateMessageId('error', message.data?.message_id),
              role: 'assistant',
              content: errorMessage,
              timestamp: new Date(),
              isThinking: false
            };

            setMessages(prev => {
              // Remove any thinking messages and add error message
              const filtered = prev.filter(msg => msg.id !== 'agent-thinking' && !msg.isThinking);
              const updated = [...filtered, assistantErrorMessage];
              onMessageUpdate?.(updated);
              return updated;
            });
          } else {
            // Catch-all for unhandled events
            console.log('🟡 UNHANDLED EVENT TYPE:', message.type, message.data);
          }
          
          // Handle loading/streaming states for other completion events
          if (message.type === 'dev_conversation_complete' || message.type === 'dev_message_end' || message.type === 'dev_text_end') {
            setIsLoading(false);
            setIsStreaming(false);
          }
        },
        onConnect: () => {
          console.log('✅ WebSocket connected for dev agent');
        },
        onDisconnect: () => {
          console.log('🔌 WebSocket disconnected for dev agent');
        },
        onError: () => {
          console.error('❌ WebSocket error for dev agent');
        }
      });
      console.log('✅ WebSocket connection initiated successfully');
    } catch (error) {
      console.error('❌ Error connecting to WebSocket:', error);
    }
    
    // Check for pending credential requests (for state persistence)
    if (threadId) {
      await checkForPendingCredentialRequest(threadId);
    }
    
    console.log('✅ selectThread completed successfully');
  }, [currentThreadId, webSocket, handleReconnection, onMessageUpdate, removeWaitingMessage, checkForPendingCredentialRequest]);

  // Send message
  const sendMessage = useCallback(async (content: string, images?: string[], selectedFileIds?: string[], threadId?: string, artifactIds?: string[]) => {
    const effectiveThreadId = threadId || currentThreadId;
    console.log('🚀 sendMessage called with:', { content, images, selectedFileIds, artifactIds, threadId, currentThreadId, effectiveThreadId, projectId });
    
    if (!effectiveThreadId) {
      console.error('❌ No thread selected - cannot send message');
      return;
    }

    if (!projectId) {
      console.error('❌ No project ID - cannot send message');
      return;
    }

    // Resolve attachment names for display
    let fileNames: string[] = [];
    let artifactNames: string[] = [];
    
    try {
      // Get file names if we have file IDs
      if (selectedFileIds && selectedFileIds.length > 0) {
        const fileResponse = await api.get(`/projects/${projectId}/files/`);
        const files = fileResponse.data || [];
        fileNames = selectedFileIds.map(fileId => {
          const file = files.find((f: any) => f.id === fileId);
          return file ? file.original_name || file.name || fileId : fileId;
        });
      }
      
      // Get artifact names if we have artifact IDs  
      if (artifactIds && artifactIds.length > 0) {
        const artifactResponse = await api.get(`/projects/${projectId}/artifacts/`);
        const artifacts = artifactResponse.data || [];
        artifactNames = artifactIds.map(artifactId => {
          const artifact = artifacts.find((a: any) => (a.id || a._id) === artifactId);
          if (artifact) {
            const title = artifact.title || artifactId;
            return artifact.version ? `${title} - V${artifact.version}` : title;
          }
          return artifactId;
        });
      }
    } catch (error) {
      console.warn('❌ Failed to resolve attachment names:', error);
      // Fallback to IDs if name resolution fails
      fileNames = selectedFileIds || [];
      artifactNames = artifactIds || [];
    }

    // Immediately add user message to UI with attachment info
    const userMessage: Message = {
      id: generateMessageId('user'),
      role: 'user',
      content: content,
      timestamp: new Date(),
      images: images || [],
      fileIds: selectedFileIds || [],
      artifactIds: artifactIds || [],
      fileNames: fileNames,
      artifactNames: artifactNames
    };

    console.log('📝 Adding user message to UI:', userMessage);
    setMessages(prev => {
      // Remove any existing waiting message and add user message
      const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
      const updated = [...filtered, userMessage];
      onMessageUpdate?.(updated);
      return updated;
    });

    // Add thinking message from agent
    const thinkingMessage: Message = {
      id: 'agent-thinking',
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isThinking: true,
      thinkingMessage: 'Starting BlueGenAI Agent workflow...'
    };

    console.log('🔥🔥🔥 CREATING THINKING MESSAGE:', thinkingMessage);
    setMessages(prev => {
      console.log('🔥🔥🔥 Messages before adding thinking:', prev.length);
      const updated = [...prev, thinkingMessage];
      console.log('🔥🔥🔥 Messages after adding thinking:', updated.length);
      console.log('🔥🔥🔥 Thinking message ID:', thinkingMessage.id);
      onMessageUpdate?.(updated);
      return updated;
    });

    const messageData = {
      content: content,
      image_urls: images || [],
      file_ids: selectedFileIds || [],
      artifact_ids: artifactIds || [],
      thread_id: effectiveThreadId
    };

    const apiUrl = `/dev-agent/projects/${projectId}/chat/start`;
    console.log('🌐 About to call API:', { apiUrl, messageData });

    try {
      setIsLoading(true);
      setIsStreaming(true);  // Enable streaming state to show stop button
      console.log('📡 Making API call to start conversation...');
      const response = await api.post(apiUrl, messageData);
      console.log('✅ API call successful:', response);
      console.log('🕐 Expecting WebSocket messages to start arriving...');
      const wsState = webSocket?.socketState;
      console.log('🔥🔥🔥 WEBSOCKET CONNECTION STATUS:', wsState);
      console.log('🔥🔥🔥 CURRENT THREAD ID:', effectiveThreadId);
      console.log('🔥🔥🔥 PROJECT ID:', projectId);
      setLastMessageTimestamp(Date.now());
      
    } catch (error) {
      console.error('❌ Error sending message:', error);
      console.error('Error details:', {
        message: (error as any).message,
        response: (error as any).response?.data,
        status: (error as any).response?.status
      });
      setIsLoading(false);
      setIsStreaming(false);  // Reset streaming state on error
      // Remove thinking message on error
      setMessages(prev => {
        const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
        onMessageUpdate?.(filtered);
        return filtered;
      });
    }
  }, [currentThreadId, projectId, onMessageUpdate]);

  // Start conversation (for compatibility with existing code)
  const startConversation = useCallback(async (
    threadId: string, 
    message: string, 
    fileIds?: string[], 
    artifactIds?: string[], 
    imageUrls?: string[]
  ) => {
    console.log('🎬 startConversation called with:', { 
      threadId, 
      message: message.substring(0, 50) + '...', 
      currentThreadId,
      fileIds, 
      artifactIds, 
      imageUrls 
    });
    
    if (threadId !== currentThreadId) {
      console.log('🔄 Need to select different thread:', threadId);
      await selectThread(threadId);
    } else {
      console.log('✅ Already on correct thread:', threadId);
    }
    
    console.log('📤 About to send message...');
    await sendMessage(message, imageUrls, fileIds, threadId, artifactIds);
    console.log('✅ startConversation completed');
  }, [currentThreadId, selectThread, sendMessage]);

  // Stop conversation (for compatibility with existing code)
  const stopConversation = useCallback(async () => {
    if (!currentThreadId) return;
    
    try {
      console.log('🛑 Stopping conversation:', currentThreadId);
      await api.post(`/dev-agent/projects/${projectId}/conversations/${currentThreadId}/stop`);
      
      // Reset UI state
      setIsLoading(false);
      setIsStreaming(false);
      setWaitingState('none');
      
      // Clear any active timeouts
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
      
      // Remove thinking message and reload conversation to get final state
      removeWaitingMessage();
      await loadMessages(currentThreadId, true); // Force reload after stop
      
      console.log('✅ Conversation stopped and reloaded');
    } catch (error) {
      console.error('Error stopping conversation:', error);
      // Still reset UI state even if stop call fails
      setIsLoading(false);
      setIsStreaming(false);
      setWaitingState('none');
    }
  }, [currentThreadId]);

  // Enhanced workflow methods
  const startEnhancedWorkflow = useCallback(async (
    content: string, 
    images?: string[], 
    selectedFileIds?: string[],
    model?: ModelPreference
  ) => {
    if (!projectId || !content.trim()) return;

    console.log('🎯 Starting enhanced workflow:', content);
    
    try {
      setIsLoading(true);
      setWorkflowMode('elaboration');
      
      // Create thread if needed
      let effectiveThreadId = currentThreadId;
      if (!effectiveThreadId) {
        const threadResponse = await api.post(`/dev-agent/projects/${projectId}/threads/`, {
          title: content.substring(0, 50) + (content.length > 50 ? '...' : '')
        });
        effectiveThreadId = threadResponse.data.thread_id;
        setCurrentThreadId(effectiveThreadId);
      }

      // Start enhanced workflow
      const response = await api.post(`/dev-agent/projects/${projectId}/chat/start`, {
        content,
        image_urls: images || [],
        file_ids: selectedFileIds || [],
        thread_id: effectiveThreadId,
        model_preference: model
      });

      console.log('✅ Enhanced workflow started:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Enhanced workflow failed:', error);
      setWorkflowMode('chat');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, currentThreadId]);

  const refineElaboration = useCallback(async (sessionId: string, refinement: string, threadId?: string, model?: ModelPreference) => {
    try {
      setIsLoading(true);
      console.log('🔄 Refining elaboration:', sessionId, refinement);
      
      // Use provided threadId or fall back to currentThreadId
      const effectiveThreadId = threadId || currentThreadId;
      console.log('🔄 Using thread_id:', effectiveThreadId);

      if (!effectiveThreadId) {
        throw new Error('No thread ID available for refinement - please provide threadId parameter');
      }

      const response = await api.post(`/dev-agent/projects/${projectId}/elaboration/${sessionId}/refine`, {
        refinement,
        thread_id: effectiveThreadId,
        project_id: projectId,
      }, {
        params: {
          model_preference: model
        }
      });

      console.log('✅ Elaboration refined:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Elaboration refinement failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [currentThreadId, projectId]);

  const approveElaboration = useCallback(async (sessionId: string, threadId?: string, model?: ModelPreference) => {
    try {
      setIsLoading(true);
      console.log('✅ Approving elaboration:', sessionId);
      
      // Use provided threadId or fall back to currentThreadId
      const effectiveThreadId = threadId || currentThreadId;
      console.log('✅ Using thread_id:', effectiveThreadId);

      if (!effectiveThreadId) {
        throw new Error('No thread ID available for approval - please provide threadId parameter');
      }

      const response = await api.post(`/dev-agent/projects/${projectId}/elaboration/${sessionId}/approve`, {
        thread_id: effectiveThreadId,
        project_id: projectId,
      }, {
        params: {
          model_preference: model
        }
      });

      console.log('✅ Elaboration approved:', response.data);
      
      // Switch to development mode and enable stop button
      setWorkflowMode('development');
      setIsElaborationActive(false);
      setIsStreaming(true); // Enable stop button for development phase
      console.log('🛑 Elaboration approved - enabling stop button for development');
      
      return response.data;

    } catch (error) {
      console.error('❌ Elaboration approval failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [currentThreadId]);

  const getTokenSummary = useCallback(async (threadId: string) => {
    try {
      console.log('📊 Getting token summary:', threadId);
      
      const response = await api.get(`/dev-agent/projects/${projectId}/threads/${threadId}/token-summary`);
      
      console.log('✅ Token summary retrieved:', response.data);
      setTokenSummary(response.data.token_summary);
      
      return response.data.token_summary;

    } catch (error) {
      console.error('❌ Token summary retrieval failed:', error);
      throw error;
    }
  }, []);

  const resetWorkflow = useCallback(async () => {
    console.log('🔄 RESETTING WORKFLOW: Starting cancellation process');
    
    // Store the session info before clearing state
    const sessionIdToCancel = elaborationSession?.session_id;
    const threadId = currentThreadId;
    
    if (sessionIdToCancel && threadId) {
      console.log('❌ CANCELLING ELABORATION:', sessionIdToCancel);
      
      try {
        console.log('🌐 Calling backend cancellation API...');
        
        // Check if this is a classification session (temporary session ID)
        const isClassificationSession = sessionIdToCancel.startsWith('classification_');
        const apiEndpoint = isClassificationSession 
          ? `/dev-agent/projects/${projectId}/conversations/${threadId}/stop-classification`
          : `/dev-agent/projects/${projectId}/conversations/${threadId}/cancel-elaboration`;
          
        console.log(`📡 Using endpoint: ${apiEndpoint} (isClassification: ${isClassificationSession})`);
        const response = await api.post(apiEndpoint);
        
        console.log('✅ Backend cancellation successful:', response.data);
          
          // Mark as cancelled locally to prevent restoration
          setCancelledElaborations(prev => new Set([...Array.from(prev), sessionIdToCancel]));
          
          // Reset UI state first
          console.log('🔄 Resetting UI state...');
          console.log('📊 BEFORE RESET: isElaborationActive =', isElaborationActive);
          setWorkflowMode('chat');
          setElaborationSession(null);
          setIsElaborationActive(false);
          setCurrentElaboration('');
          setTokenSummary(null);
          console.log('📊 AFTER RESET: isElaborationActive should be FALSE');
          
          // Reload messages to get the persisted cancellation message
          console.log('📥 Reloading messages...');
          await loadMessages(threadId, true);
          
          // Auto-scroll to show the new message
          setTimeout(() => {
            const messagesEnd = document.querySelector('[data-messages-end]');
            messagesEnd?.scrollIntoView({ behavior: 'smooth' });
          }, 300);
          
          console.log('✅ Cancellation process completed successfully');
      } catch (error) {
        console.error('❌ Error during cancellation:', error);
        
        // Mark as cancelled locally anyway
        setCancelledElaborations(prev => new Set([...Array.from(prev), sessionIdToCancel]));
        
        // Reset UI state
        console.log('🔄 Resetting UI state (error fallback)...');
        console.log('📊 BEFORE RESET: isElaborationActive =', isElaborationActive);
        setWorkflowMode('chat');
        setElaborationSession(null);
        setIsElaborationActive(false);
        setCurrentElaboration('');
        setTokenSummary(null);
        console.log('📊 AFTER RESET: isElaborationActive should be FALSE');
        
        // Add fallback cancellation message
        const cancellationMessage: Message = {
          id: generateMessageId('cancellation'),
          role: 'assistant',
          content: '**Requirement elaboration cancelled** by user request.',
          timestamp: new Date(),
          isThinking: false
        };
        
        setMessages(prev => {
          const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
          const updated = [...filtered, cancellationMessage];
          onMessageUpdate?.(updated);
          return updated;
        });
        
        console.log('📝 Added fallback cancellation message due to error');
      }
    } else {
      console.log('⚠️ No elaboration session to cancel, just resetting UI state');
      // Just reset UI state if no session to cancel
      setWorkflowMode('chat');
      setElaborationSession(null);
      setIsElaborationActive(false);
      setCurrentElaboration('');
      setTokenSummary(null);
    }
  }, [elaborationSession, currentThreadId, loadMessages, onMessageUpdate]);

  // Silent reset for when thread is deleted - only resets local state, no backend calls
  const resetWorkflowSilently = useCallback(() => {
    console.log('🔇 SILENTLY RESETTING WORKFLOW: No backend calls, just local state reset');
    
    const sessionIdToCancel = elaborationSession?.session_id;
    
    if (sessionIdToCancel) {
      // Mark as cancelled locally to prevent restoration
      setCancelledElaborations(prev => new Set([...Array.from(prev), sessionIdToCancel]));
    }
    
    // Reset UI state only
    console.log('🔄 Resetting UI state silently...');
    console.log('📊 BEFORE RESET: isElaborationActive =', isElaborationActive);
    setWorkflowMode('chat');
    setElaborationSession(null);
    setIsElaborationActive(false);
    setCurrentElaboration('');
    setTokenSummary(null);
    console.log('📊 AFTER RESET: isElaborationActive should be FALSE');
    
    console.log('✅ Silent workflow reset completed');
  }, [elaborationSession, isElaborationActive]);

  // Add a thinking message to show the system is processing
  const addProcessingMessage = useCallback((message: string = 'Processing your request...') => {
    const thinkingMessage: Message = {
      id: 'agent-thinking',
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isThinking: true,
      thinkingMessage: message
    };

    setMessages(prev => {
      // Remove any existing thinking message
      const filtered = prev.filter(msg => msg.id !== 'agent-thinking');
      const updated = [...filtered, thinkingMessage];

      console.log('📝 ADD_PROCESSING_MESSAGE: Added processing message');
      onMessageUpdate?.(updated);
      return updated;
    });
  }, [onMessageUpdate]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (waitingTimeoutRef.current) {
        window.clearTimeout(waitingTimeoutRef.current);
      }
      if (messageTimeoutRef.current) {
        window.clearTimeout(messageTimeoutRef.current);
      }
      webSocket.disconnect();
    };
  }, []);

  return {
    // State
    messages,
    currentThreadId,
    isLoading,
    isStreaming,
    waitingState,
    isCheckingState,
    currentConversationState,
    hasMoreMessages,
    
    // Enhanced workflow state
    workflowMode,
    elaborationSession,
    isElaborationActive,
    currentElaboration,
    tokenSummary,
    cancelledElaborations,
    
    // Actions
    selectThread,
    setCurrentThreadId,
    sendMessage,
    addProcessingMessage,
    loadMessages,
    loadMoreMessages,
    startConversation,
    stopConversation,
    resumeConversation,
    discardConversation,
    waitMore,
    
    // State setters (for special cases)
    setMessages,
    setIsLoading,
    setIsStreaming,

    // Enhanced workflow actions
    startEnhancedWorkflow,
    refineElaboration,
    approveElaboration,
    getTokenSummary,
    resetWorkflow,
    resetWorkflowSilently,
    
    // WebSocket state
    socketState: webSocket.socketState,
    lastSocketEvent: webSocket.lastSocketEvent,
    isReconnecting: webSocket.isReconnecting,
    reconnectAttempt: webSocket.reconnectAttempt,
    
    // Credential management
    credentialCaptureRequest,
    submitCapturedCredentials,
    clearCredentialCaptureRequest: useCallback(() => {
      console.log('❌ Credential capture cancelled by user');
      setCredentialCaptureRequest(null);
      // Resume the 60-second timeout since user interaction is complete
      resumeWaitingTimeout();
    }, [resumeWaitingTimeout])
  };
}